import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";
import { dedupeInFlight } from "src/utils/staleGuard";

// Estado del onboarding legal del restaurante. Lo refrescamos al iniciar
// sesión y tras cada aceptación. Cuando `pending.length > 0` y el usuario
// ya está autenticado, el LegalOnboardingGate bloquea la app con el modal.
const initialState = {
  isLoading: false,
  isLoaded: false,
  error: null,
  pending: [], // ej: ["data_protection", "verifactu"]
  currentVersions: {}, // ej: {data_protection: 1, verifactu: 2}
  isCompleted: false,

  // ─── Wizard bloque B onboarding v2 ───────────────────────────────
  //
  //   Estado del wizard. Se carga tras login (JwtProvider dispatch)
  //   y se refresca tras cada acción. `progress` = null hasta el
  //   primer fetch (mientras: nada de widget en TopBar).
  //
  wizard: {
    loading: false,
    error: null,
    progress: null,      // DtoOnboardingProgress
  },
};

const slice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    statusLoaded(state, action) {
      state.isLoading = false;
      state.isLoaded = true;
      state.pending = action.payload.pending || [];
      state.currentVersions = action.payload.currentVersions || {};
      state.isCompleted = !!action.payload.isCompleted;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    reset(state) {
      Object.assign(state, initialState);
    },

    // Wizard bloque B
    wizardStart(state) { state.wizard.loading = true; state.wizard.error = null; },
    wizardLoaded(state, action) {
      state.wizard.loading = false;
      state.wizard.progress = action.payload;
    },
    wizardError(state, action) {
      state.wizard.loading = false;
      state.wizard.error = action.payload;
    },
  },
});

export default slice.reducer;
export const { reset: resetOnboarding } = slice.actions;

/**
 * Lee el estado actual del onboarding legal. Llamar tras login y al recargar.
 * Si la respuesta es 401/403 (no autenticado) lo silenciamos — el LegalGate
 * sólo monta cuando ya hay sesión válida.
 */
export function fetchLegalStatus() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get("/onboarding/legal/status");
      dispatch(slice.actions.statusLoaded(res.data));
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error onboarding"));
    }
  };
}

/**
 * Acepta uno o varios documentos. El backend graba audit-trail (timestamp,
 * IP, nombre firma) y devuelve el estado refrescado. Si ya no quedan
 * pendientes, `isCompleted` será true y el gate se cerrará automáticamente.
 */
export function acceptLegalDocuments({ documentKeys, signerFullName }) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.post("/onboarding/legal/accept", {
        documentKeys,
        signerFullName,
      });
      dispatch(slice.actions.statusLoaded(res.data));
      return res.data;
    } catch (e) {
      dispatch(slice.actions.hasError(e?.message || "Error firmando"));
      throw e;
    }
  };
}

// ─── Wizard bloque B — thunks ─────────────────────────────────────────

export function fetchOnboardingProgress() {
  return async (dispatch) => {
    // 2026-09-11 · dedupeInFlight: useOnboardingProgress + AlertsCenter
    // + JwtProvider llaman este thunk en el mismo tick al bootstrap.
    return dedupeInFlight("onboarding/progress", async () => {
      dispatch(slice.actions.wizardStart());
      try {
        const res = await axios.get("/onboarding/progress");
        dispatch(slice.actions.wizardLoaded(res.data));
        return res.data;
      } catch (e) {
        dispatch(slice.actions.wizardError(e?.message || "Error onboarding"));
        return null;
      }
    });
  };
}

export function patchOnboardingProgress(dto) {
  return async (dispatch) => {
    const res = await axios.patch("/onboarding/progress", dto);
    dispatch(slice.actions.wizardLoaded(res.data));
    return res.data;
  };
}

/** Marca un step completado; el backend avanza CurrentStep. */
export function completeOnboardingStep(step, extra = {}) {
  return async (dispatch) => {
    const res = await axios.post("/onboarding/progress/complete-step", {
      step,
      elapsedSeconds: extra.elapsedSeconds ?? null,
      dataJson: extra.dataJson ?? null,
    });
    dispatch(slice.actions.wizardLoaded(res.data));
    return res.data;
  };
}

export function dismissOnboardingForToday() {
  return async (dispatch) => {
    const res = await axios.post("/onboarding/dismiss");
    dispatch(slice.actions.wizardLoaded(res.data));
  };
}

export function scheduleInstall(scheduledAt, url) {
  return async (dispatch) => {
    const res = await axios.post("/onboarding/schedule-install", {
      scheduledAt,
      url,
    });
    dispatch(slice.actions.wizardLoaded(res.data));
  };
}

// Reemplazo del flujo Calendly — el user pide que le llamemos.
// Backend reusa el flag `MeetingScheduledAt` para marcar "hay contacto
// pendiente"; usa `User.NotificationPhones` como teléfono destino. El
// admin recibe email con nombre + teléfono para llamar.
export function requestInstallCallback() {
  return async (dispatch) => {
    const res = await axios.post("/onboarding/request-callback");
    dispatch(slice.actions.wizardLoaded(res.data));
  };
}

/** Evento de funnel — fire-and-forget, no dispara reducer (sin re-render). */
export function logOnboardingEvent(step, action, extra = {}) {
  return async () => {
    try {
      await axios.post("/onboarding/events", {
        step,
        action,
        elapsedSeconds: extra.elapsedSeconds ?? null,
        dataJson: extra.dataJson ?? null,
      });
    } catch { /* silently ignore — no queremos romper UX por telemetría */ }
  };
}
